import { uniqueFileIds, parseImageFileIds } from '@/features/components/common-image/imageFileIdsUtil.js';

/**
 * 判断 reportContent 解析后的对象是否包含有效上报草稿（不含仅由详情带出、未入库的字段）
 */
export function hasReportContentFilled (parsed) {
  if (!parsed || typeof parsed !== 'object') return false;
  const textKeys = ['proposed', 'emergency', 'wbUser', 'wbUserPhone', 'xfkUser', 'xfkUserPhone', 'faultDesc'];
  for (let i = 0; i < textKeys.length; i++) {
    const v = parsed[textKeys[i]];
    if (v != null && String(v).trim() !== '') return true;
  }
  const ex = parsed.reportExtraImages;
  if (typeof ex === 'string' && ex.split(',').map(s => s.trim()).filter(Boolean).length) return true;
  if (Array.isArray(ex) && ex.filter(Boolean).length) return true;
  return false;
}

/**
 * 待处理单打开弹窗时：有上报草稿则默认「上报甲方」，否则自行处理
 */
export function defaultHandleTypeForPending (fault) {
  if (!fault || fault.status !== 'pending') {
    return fault && fault.handleType ? fault.handleType : 'self';
  }
  let parsed = {};
  if (fault.reportContent) {
    try {
      parsed = JSON.parse(fault.reportContent);
    } catch (e) {
      parsed = {};
    }
  }
  if (hasReportContentFilled(parsed)) return 'report';
  return fault.handleType || 'self';
}

/**
 * 安全解析 JSON，失败返回空对象
 */
export function parseReportContentJson (reportContent) {
  if (!reportContent) return {};
  try {
    return JSON.parse(reportContent);
  } catch (e) {
    return {};
  }
}

/**
 * 从列表 fault 初始化上报补充表单（弹窗 data 首次构造）
 */
export function buildInitialReportFormFromFault (fault) {
  const initReportForm = {
    proposed: '',
    emergency: '',
    wbUser: '',
    wbUserPhone: '',
    xfkUser: '',
    xfkUserPhone: '',
    faultDesc: '',
    reportExtraImages: []
  };
  if (fault.reportContent) {
    try {
      const o = JSON.parse(fault.reportContent);
      Object.assign(initReportForm, o);
      const ex = o.reportExtraImages;
      if (typeof ex === 'string') {
        initReportForm.reportExtraImages = uniqueFileIds(ex.split(',').map(s => s.trim()).filter(Boolean));
      } else if (Array.isArray(ex)) {
        initReportForm.reportExtraImages = uniqueFileIds(ex.filter(Boolean));
      } else {
        initReportForm.reportExtraImages = [];
      }
    } catch (e) { /* 忽略解析失败 */ }
  }
  if (!String(initReportForm.faultDesc || '').trim() && fault.faultDesc) {
    initReportForm.faultDesc = fault.faultDesc;
  }
  return initReportForm;
}

/**
 * 详情加载后与草稿合并：返回新表单对象及是否应将处置方式切为「上报甲方」
 */
export function mergeReportFormFromDetail (detail) {
  if (!detail) return null;
  let parsed = {};
  if (detail.reportContent) {
    try {
      parsed = JSON.parse(detail.reportContent);
    } catch (e) {
      parsed = {};
    }
  }
  const next = {
    proposed: '',
    emergency: '',
    wbUser: '',
    wbUserPhone: '',
    xfkUser: '',
    xfkUserPhone: '',
    faultDesc: '',
    reportExtraImages: []
  };
  Object.assign(next, parsed);
  if (!String(next.faultDesc || '').trim()) {
    next.faultDesc = detail.faultDesc || '';
  }
  const ex = next.reportExtraImages;
  if (typeof ex === 'string') {
    next.reportExtraImages = uniqueFileIds(ex.split(',').map(s => s.trim()).filter(Boolean));
  } else if (!Array.isArray(ex)) {
    next.reportExtraImages = [];
  } else {
    next.reportExtraImages = uniqueFileIds(ex.filter(Boolean));
  }
  const shouldSetHandleTypeReport = detail.status === 'pending' && hasReportContentFilled(parsed);
  return { next, shouldSetHandleTypeReport };
}

/**
 * 已上报单：从解析后的 reportContent 构建只读展示对象
 */
export function buildReportContentForRead (parsed) {
  const p = parsed || {};
  return {
    faultDesc: p.faultDesc || '',
    proposed: p.proposed || '',
    emergency: p.emergency || '',
    wbUser: p.wbUser || '',
    wbUserPhone: p.wbUserPhone || '',
    xfkUser: p.xfkUser || '',
    xfkUserPhone: p.xfkUserPhone || ''
  };
}

/**
 * 已上报单：从详情 reportContent 解析补充现场图 fileId 列表
 */
export function parseReportExtraPhotoIdsFromDetail (detail) {
  if (!detail || detail.status !== 'reported' || !detail.reportContent) return [];
  try {
    const o = JSON.parse(detail.reportContent);
    const raw = o && o.reportExtraImages;
    return uniqueFileIds(
      typeof raw === 'string'
        ? raw.split(',').map(s => s.trim()).filter(Boolean)
        : (Array.isArray(raw) ? raw.filter(Boolean) : [])
    );
  } catch (e) {
    return [];
  }
}

/**
 * 故障描述展示：真实换行 + pre-wrap 才能分段；接口或历史数据可能把换行存成字面量「\n」。
 * 小程序端已用「；」+ 真实换行拼接多项，此处用 (?!\n) 避免在已有换行后再插一行。
 */
export function formatFaultDesc (desc) {
  if (!desc) return '-';
  let s = String(desc);
  s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  s = s.replace(/\\n/g, '\n');
  s = s.replace(/；(?!\n)/g, '；\n').replace(/;(?!\n)/g, ';\n');
  return s.trim();
}

/**
 * 序列化上报 Word 补充项为 reportContent JSON 字符串
 */
export function serializeReportContent (reportForm) {
  const data = {};
  Object.keys(reportForm).forEach(k => {
    if (k === 'reportExtraImages') return;
    const v = reportForm[k];
    if (v) data[k] = v;
  });
  const extra = reportForm.reportExtraImages;
  if (Array.isArray(extra) && extra.length) {
    data.reportExtraImages = uniqueFileIds(extra.filter(Boolean)).join(',');
  }
  return JSON.stringify(data);
}

/**
 * 并发拉取多张图片的 base64 缩略图（与 ids 顺序一致）
 */
export async function fetchImageThumbRecordsByIds (ids, axios, headers, base) {
  if (!ids || !ids.length) return [];
  const results = await Promise.all(
    ids.map((fileId) =>
      axios.get(`${base}${fileId}`, { headers })
        .then((r) => {
          const data = r.data && r.data.data ? r.data.data : '';
          if (data) return { fileId, url: 'data:image/png;base64,' + data };
          return null;
        })
        .catch((e) => {
          console.warn('加载图片失败', fileId, e);
          return null;
        })
    )
  );
  return results.filter(Boolean);
}

/**
 * 从 faultImages 字段解析 id 列表后拉取缩略图
 */
export async function loadFaultPhotoThumbRecords (faultImages, axios, headers, base) {
  const ids = parseImageFileIds(faultImages);
  if (!ids.length) return [];
  return fetchImageThumbRecordsByIds(ids, axios, headers, base);
}

/**
 * 从已上报详情的 reportContent 中解析补充图 id 后拉取缩略图
 */
export async function loadReportExtraThumbRecordsFromReportContent (reportContent, axios, headers, base) {
  let raw;
  try {
    const o = JSON.parse(reportContent);
    raw = o && o.reportExtraImages;
  } catch (e) {
    return [];
  }
  const ids = uniqueFileIds(
    typeof raw === 'string'
      ? raw.split(',').map(s => s.trim()).filter(Boolean)
      : (Array.isArray(raw) ? raw.filter(Boolean) : [])
  );
  if (!ids.length) return [];
  return fetchImageThumbRecordsByIds(ids, axios, headers, base);
}
